import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Bridges the fork's `UserProfileService` into React. The profile is fetched
 * once at viewer startup (see the Mode route) and exposed here for conditional
 * rendering of viewer features. `profile` is `null` until it resolves and stays
 * `null` if the backend request fails.
 *
 * `features` is the per-user feature-flag map from the RIS profile, e.g.
 * `{ favoritos: true }`. Use `hasFeature('favoritos')` to gate a feature.
 */
type UserProfileValue = {
  profile: Record<string, unknown> | null;
  features: Record<string, boolean>;
  hasFeature: (feature: string) => boolean;
};

const UserProfileContext = createContext<UserProfileValue>({
  profile: null,
  features: {},
  hasFeature: () => false,
});

const PROFILE_UPDATED = 'event::userProfile:updated';

export function UserProfileProvider({ children, service }) {
  const [profile, setProfile] = useState(service?.getProfile?.() ?? null);

  useEffect(() => {
    if (!service) {
      return;
    }
    setProfile(service.getProfile() ?? null);
    const { unsubscribe } = service.subscribe(PROFILE_UPDATED, newProfile => {
      setProfile(newProfile ?? null);
    });
    return unsubscribe;
  }, [service]);

  const value = useMemo<UserProfileValue>(() => {
    const features = (profile?.features as Record<string, boolean>) ?? {};
    return {
      profile,
      features,
      hasFeature: (feature: string) => features[feature] === true,
    };
  }, [profile]);

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
}

export default UserProfileProvider;

UserProfileProvider.propTypes = {
  children: PropTypes.any,
  service: PropTypes.shape({
    getProfile: PropTypes.func,
    subscribe: PropTypes.func,
  }).isRequired,
};

export const useUserProfile = () => useContext(UserProfileContext);
